/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useRouteMatch, useHistory } from 'react-router-dom';
import { Alert, Button, Modal, Checkbox, ButtonGroup } from 'react-bootstrap';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Toggle from 'react-bootstrap-toggle';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import { pick, startCase } from 'lodash';
import { produce } from 'immer';
import Papa from 'papaparse';
import pLimit from 'p-limit';
import ContentPage from '../ContentPage';
import UserService from '../../services/UserService';
import SidebarContainer from '../SidebarContainer';
import ErrorMessage from '../../components/ErrorMessage';
import SuccessMessage from '../../components/SuccessMessage';
import usersActions from '../../redux/user/actions';
import ToggleablePassword from '../../components/ToggleablePassword/ToggleablePassword';
import './UserImportEditor.scss';

function UserImportEditor({
  dealer,
  currentUser,
  users,
  error: reduxError,
  actions,
}) {
  const history = useHistory();
  const { importType } = useRouteMatch().params;

  const csvUploader = useRef();
  const userImportContainer = useRef();

  const [fetching, setFetching] = useState();
  const [errorMessage, setErrorMessage] = useState();
  const [successMessage, setSuccessMessage] = useState();

  const [importUsers, setImportUsers] = useState([]);
  const [importUsersNeedsDuplicateCheck, setImportUsersNeedsDuplicateCheck] =
    useState(false);

  const DEFAULT_PASSWORD_SUFFIX = '1234567890';

  useEffect(() => {
    if ((dealer || {}).id) {
      // get all users for this dealer for duplicate checking
      actions.usersRequest(dealer.id);

      // if this page was opened to import users from the crm, get the users from the crm
      if (importType === 'crm') {
        setFetching(true);
        UserService.getCRMEntries(dealer.id)
          .then((newImportUsers) => {
            // save the crm users to the local state, with some defaults
            setImportUsers(
              newImportUsers.map((newCrmUser) => ({
                ...newCrmUser,
                shouldImport: false,
                password:
                  newCrmUser.firstName.toLowerCase() + DEFAULT_PASSWORD_SUFFIX,
              })),
            );
            setImportUsersNeedsDuplicateCheck(true);
          })
          .catch((error) => {
            setErrorMessage(error);
          })
          .finally(() => setFetching(false));
      }
    }
  }, [actions, dealer, importType]);
  // helper function for updating importUsers
  const updateImportUsers = (apply) =>
    setImportUsers(
      produce((draft) => {
        apply(draft);
      }),
    );

  useEffect(() => {
    // if we need to do duplicate checking, and users have been retrieve, check importUsers against users for duplicates
    if (importUsersNeedsDuplicateCheck && (users || []).length > 0) {
      setImportUsersNeedsDuplicateCheck(false);

      // get for duplicates by crmUserId
      const duplicateCrmIdUserIndicies = users
        .map((u) => importUsers.findIndex((iu) => u.crmUserId === iu.crmUserId))
        .filter((i) => i !== -1);
      if (duplicateCrmIdUserIndicies.length > 0) {
        updateImportUsers((newImportUsers) => {
          duplicateCrmIdUserIndicies.forEach((duplicateUserIndex) => {
            newImportUsers[duplicateUserIndex].shouldImport = false;
            newImportUsers[duplicateUserIndex].foundCrmIdDuplicate = true;
          });
        });
      }
      // get for duplicates by email
      const duplicateEmailUserIndicies = users
        .map((u) =>
          importUsers.findIndex(
            (iu) => u.email.toLowerCase() === iu.email.toLowerCase(),
          ),
        )
        .filter((i) => i !== -1);
      if (duplicateEmailUserIndicies.length > 0) {
        updateImportUsers((newImportUsers) => {
          duplicateEmailUserIndicies.forEach((duplicateUserIndex) => {
            newImportUsers[duplicateUserIndex].shouldImport = false;
            newImportUsers[duplicateUserIndex].foundEmailDuplicate = true;
          });
        });
      }
    }
  }, [users, importUsersNeedsDuplicateCheck, importUsers]);

  /**
   * Imports a CSV file selected in csvUploader
   */
  const importCSV = () => {
    setFetching(true);
    try {
      setErrorMessage(); // clear any error message before importing
      const file = csvUploader.current.files[0];
      if (file) {
        const reader = new FileReader();
        reader.readAsText(file, 'UTF-8');
        reader.onload = (evt) => {
          try {
            // parse the csv file
            const { data, errors } = Papa.parse(evt.target.result, {
              skipEmptyLines: true,
              header: true,
            });
            if ((errors || []).length > 0) {
              setErrorMessage(
                `Failed to process CSV: ${errors
                  .map((e) => e.message)
                  .join(',')}`,
              );
            } else {
              // map data from the csv file to our format, with some defaults
              setImportUsers(
                data.map((d) => ({
                  password:
                    `${(d || {}).firstName}`.toLowerCase() +
                    DEFAULT_PASSWORD_SUFFIX,
                  shouldImport:
                    `${(d || {}).shouldImport}`.toLowerCase() !== 'false',
                  roles: (d || {}).roles ? d.roles.split(',') : [],
                  ...pick(d, [
                    'crmUserId',
                    'firstName',
                    'lastName',
                    'email',
                    'phone',
                    'password',
                  ]),
                })),
              );
              setImportUsersNeedsDuplicateCheck(true);
            }
          } catch (error) {
            setErrorMessage('Invalid CSV');
          }
        };
        reader.onerror = () => setErrorMessage('Error reading file');
      }
    } catch (error) {
      setErrorMessage('Error reading file');
    } finally {
      setFetching(false);
    }
  };

  const [savingCount, setSavingCount] = useState();
  /**
   * Saves importUsers in the backend
   */
  const saveUsers = async () => {
    setErrorMessage(null);
    userImportContainer.current.scrollTop = 0; // scroll to the top

    const usersToImport = importUsers.filter(
      (importUser) => importUser.shouldImport,
    );
    if (usersToImport.find((importUser) => !importUser.email)) {
      setErrorMessage('All users being imported must have an email address');
      return;
    }
    if (usersToImport.find((importUser) => !importUser.password)) {
      setErrorMessage('All users being imported must have a password');
      return;
    }
    if (usersToImport.find((importUser) => !(importUser.roles || []).length)) {
      setErrorMessage('All users being imported must at least one role');
      return;
    }

    setSavingCount(0);
    let savedCount = 0;
    try {
      const limit = pLimit(5); // limit to 5 active requests at a time
      await Promise.all(
        usersToImport.map((importUser) =>
          limit(async () => {
            await UserService.createUser({
              firstName: importUser.firstName,
              lastName: importUser.lastName,
              email: importUser.email,
              username: importUser.email,
              phone: importUser.phone,
              password: importUser.password,
              roles: importUser.roles,
              crmUserId: importUser.crmUserId,
              dealer_id: dealer.id,
            });
            savedCount += 1;
            setSavingCount(savedCount);
          }),
        ),
      );
      setSuccessMessage(`${savedCount} users saved`);
    } catch (error) {
      setErrorMessage(error);
    } finally {
      setSavingCount(undefined);
    }
  };

  const importFormatter = (shouldImport, row) => (
    <div className="import-cell">
      <Toggle
        on="Import"
        off="Ignore"
        className="toggle-switch"
        active={shouldImport}
        onClick={(value) =>
          updateImportUsers((newImportUsers) => {
            newImportUsers[
              importUsers.findIndex((c) => c === row)
            ].shouldImport = value;
          })
        }
      />
      <span className="import-cell-message">
        {row.foundCrmIdDuplicate && 'Found a duplicate user by CRM User ID'}
      </span>
      <span className="import-cell-message">
        {row.foundEmailDuplicate && 'Found a duplicate user by email'}
      </span>
    </div>
  );

  const inputFormatter = React.useCallback(
    (prop, placeholder) =>
      // This is not unstable as its inside a `useCallback`, but the lint rule
      // doesn't pick up on that
      // eslint-disable-next-line react/no-unstable-nested-components
      function FormatInput(propValue, row) {
        return (
          <input
            value={propValue}
            placeholder={placeholder}
            onChange={(event) => {
              const newValue = event.target.value;
              updateImportUsers((newImportUsers) => {
                newImportUsers[importUsers.findIndex((c) => c === row)][prop] =
                  newValue;
              });
            }}
          />
        );
      },
    [updateImportUsers],
  );

  const passwordFormatter = (propValue, row) => (
    <ToggleablePassword
      value={propValue}
      placeholder="Password"
      onChange={(event) => {
        const newValue = event.target.value;
        updateImportUsers((newImportUsers) => {
          newImportUsers[importUsers.findIndex((c) => c === row)].password =
            newValue;
        });
      }}
    />
  );

  const [roleEditorModalData, setRoleEditorModalData] = useState();
  const [roleEditorModalSelections, setRoleEditorModalSelections] = useState(
    [],
  );
  const rolesFormatter = (roles, row) => (
    <div className="roles-cell">
      <div className="roles-list">
        {roles.map((role) => (
          <div key={role}>{startCase(role)}</div>
        ))}
      </div>
      <Button
        onClick={() => {
          setRoleEditorModalData(row);
          setRoleEditorModalSelections(row.roles || []);
        }}
      >
        Edit
      </Button>
    </div>
  );

  const setAllUsersStatus = (condition) => {
    updateImportUsers((newImportUsers) => {
      newImportUsers.forEach((user) => {
        user.shouldImport = condition;
      });
    });
  };

  const renderSelectAll = () => (
    <Button
      variant="secondary"
      onClick={() => setAllUsersStatus(true)}
      disabled={(importUsers || []).length === 0}
    >
      Select All
    </Button>
  );

  const renderClear = () => (
    <Button
      variant="secondary"
      onClick={() => setAllUsersStatus(false)}
      disabled={(importUsers || []).length === 0}
    >
      Clear
    </Button>
  );

  const OpenCsvBtn = React.useCallback(
    () => (
      <Button bsStyle="primary" onClick={() => csvUploader.current.click()}>
        Open CSV File
      </Button>
    ),
    [csvUploader],
  );

  return (
    <SidebarContainer title="User Import" logo={dealer.logo}>
      <Modal show={!!roleEditorModalData}>
        <Modal.Header>
          <Modal.Title>
            Edit Roles for {(roleEditorModalData || {}).firstName}{' '}
            {(roleEditorModalData || {}).lastName} (
            {(roleEditorModalData || {}).crmUserId})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {((currentUser || {}).accessibleRoles || []).map((role) => (
            <Checkbox
              key={role}
              checked={roleEditorModalSelections.includes(role)}
              onChange={() => {
                if (roleEditorModalSelections.includes(role)) {
                  setRoleEditorModalSelections(
                    roleEditorModalSelections.filter((r) => r !== role),
                  );
                } else {
                  setRoleEditorModalSelections([
                    ...roleEditorModalSelections,
                    role,
                  ]);
                }
              }}
            >
              {startCase(role)}
            </Checkbox>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setRoleEditorModalData(undefined)}>
            Cancel
          </Button>
          <Button
            bsStyle="primary"
            onClick={() => {
              updateImportUsers((newImportUsers) => {
                newImportUsers[
                  importUsers.findIndex((c) => c === roleEditorModalData)
                ].roles = roleEditorModalSelections;
              });
              setRoleEditorModalData(undefined);
            }}
          >
            Continue
          </Button>
        </Modal.Footer>
      </Modal>
      <div ref={userImportContainer} className="user-import-container">
        {savingCount !== undefined && (
          <Alert>
            Saving users {savingCount}/
            {importUsers.filter((importUser) => importUser.shouldImport).length}
          </Alert>
        )}
        {fetching && (
          <Alert>Getting users from {`${importType}`.toUpperCase()}</Alert>
        )}
        {(errorMessage || reduxError) && (
          <ErrorMessage error={errorMessage || reduxError} />
        )}
        {successMessage && <SuccessMessage success={successMessage} />}
        <h3>
          Found <b>{(importUsers || []).length}</b> Users in{' '}
          {`${importType}`.toUpperCase()}
        </h3>
        <input
          type="file"
          id="file"
          ref={csvUploader}
          style={{ display: 'none' }}
          onChange={(_) => importCSV()}
        />
        <ButtonGroup className="pb-10">
          {renderSelectAll()}
          {renderClear()}
        </ButtonGroup>
        <BootstrapTable
          data={importUsers}
          options={{
            insertBtn: OpenCsvBtn,
          }}
          striped
          hover
          bodyContainerClass="list-body-container"
          tableStyle={{ border: 'none' }}
          insertRow={importType === 'csv'}
          exportCSV
        >
          <TableHeaderColumn
            dataField="shouldImport"
            dataFormat={importFormatter}
            dataSort
          >
            Status
          </TableHeaderColumn>
          <TableHeaderColumn
            isKey
            dataField="crmUserId"
            dataFormat={inputFormatter('crmUserId', 'CRM User Id')}
            dataSort
          >
            CRM User Id
          </TableHeaderColumn>
          <TableHeaderColumn
            dataField="firstName"
            dataFormat={inputFormatter('firstName', 'First Name')}
            dataSort
          >
            First Name
          </TableHeaderColumn>
          <TableHeaderColumn
            dataField="lastName"
            dataFormat={inputFormatter('lastName', 'Last Name')}
            dataSort
          >
            Last Name
          </TableHeaderColumn>
          <TableHeaderColumn
            dataField="email"
            dataFormat={inputFormatter('email', 'Email')}
            dataSort
          >
            Email
          </TableHeaderColumn>
          <TableHeaderColumn
            dataField="phone"
            dataFormat={inputFormatter('phone', 'Phone')}
            dataSort
          >
            Phone
          </TableHeaderColumn>
          <TableHeaderColumn
            dataField="password"
            dataFormat={passwordFormatter}
          >
            Password
          </TableHeaderColumn>
          <TableHeaderColumn
            dataField="roles"
            dataFormat={rolesFormatter}
            dataSort
          >
            Roles
          </TableHeaderColumn>
        </BootstrapTable>

        <ButtonGroup className="footer-actions">
          <Button onClick={() => history.goBack()}>Back</Button>
          <Button bsStyle="primary" onClick={saveUsers}>
            Save
          </Button>
        </ButtonGroup>
      </div>
    </SidebarContainer>
  );
}

UserImportEditor.propTypes = {
  dealer: PropTypes.object,
  currentUser: PropTypes.object,
  users: PropTypes.array,
  actions: PropTypes.object,
  error: PropTypes.any,
};

const mapStateToProps = ({ user, dealers }) => ({
  currentUser: user.currentUser,
  dealer: dealers.dealer,
  users: user.users,
  error: user.error || dealers.error,
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      ...usersActions,
    },
    dispatch,
  ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ContentPage(UserImportEditor));
